/* Aurora — scroll reveal.

   Sections settle into place as they enter the viewport instead of snapping in. One
   IntersectionObserver serves the whole page; there is no library and no scroll listener,
   so this costs nothing while the page is idle.

   How targets are chosen — no section markup needed:
   every aurora section wraps its content in the canonical `.aurora-w--*` width element
   (40 of 46 do; the six that don't are the header, mobile menu, slideshow, marquee, wave
   divider and collection grid, none of which should reveal). The element children of that
   wrapper are the reveal targets, in document order. Where a child is a grid or carousel
   track, its items are staggered instead of the container, so a product row arrives card
   by card rather than as one block.

   Notes:
   - The CSS that hides an unrevealed element is gated on the `aurora-reveal-on` class this
     file puts on <html>. Script blocked, JS error, old browser -> nothing is ever hidden.
     Never move the initial state into unconditional CSS.
   - `prefers-reduced-motion` opts out entirely: no class, no hiding, no transition.
   - Elements above the fold on load are revealed immediately, without transition, so the
     first paint isn't an animation the visitor never asked to watch.
   - Each element is unobserved once revealed. Nothing re-hides on scroll up — reversing
     reveals is what makes a page feel busy rather than expensive.
*/
(function () {
  var MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (MOTION && MOTION.matches) return;
  if (!('IntersectionObserver' in window)) return;

  var ROOT = document.documentElement;
  var READY = 'aurora-reveal-on';
  var HIDDEN = 'aurora-reveal';
  var SHOWN = 'is-revealed';
  var INSTANT = 'is-revealed--instant';
  var MASK = 'aurora-reveal--mask';

  /* containers whose ITEMS stagger, rather than the container itself */
  var GROUP = '[class*="__grid"], [class*="__list"], [class*="__items"], [class*="__track"], [class*="__cards"], [class*="__blocks"]';
  /* never reveal: chrome, overlays, and anything that owns its own transform */
  var SKIP = 'aurora-header, aurora-mobile-menu, aurora-slideshow, .aurora-scrim, [class*="__overlay"], [class*="__panel"], [class*="drawer"], [class*="popup"], .shopify-section-header';
  /* containers that hold copy: their own children are the reveal targets */
  var TEXTBOX = /__(header|head|content|text|copy|intro|body)\b/;
  var MAX_STAGGER = 8;

  var observer = null;

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add(SHOWN);
      observer.unobserve(entry.target);
    });
  }

  function isHidden(el) {
    /* an element with no box yet (a closed accordion, an inactive slide) must not be
       marked — it would never intersect, and would stay invisible when later opened */
    return !el.offsetParent && el.offsetWidth === 0 && el.offsetHeight === 0;
  }

  var HEADING = /__(title|heading|subheading|header-text|head-text)\b/;

  function mark(el, order) {
    if (el.classList.contains(HIDDEN)) return;
    if (el.closest(SKIP)) return;
    if (isHidden(el)) return;
    el.classList.add(HIDDEN);
    if (/^H[1-6]$/.test(el.tagName) || HEADING.test(el.className)) el.classList.add(MASK);
    if (order) el.style.setProperty('--reveal-i', Math.min(order, MAX_STAGGER));
    /* already on screen at load: show it now, without playing the transition */
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      el.classList.add(SHOWN, INSTANT);
      return;
    }
    observer.observe(el);
  }

  function boxes(el) {
    var out = [];
    for (var i = 0; i < el.children.length; i++) {
      var c = el.children[i];
      if (c.tagName === 'STYLE' || c.tagName === 'SCRIPT' || c.tagName === 'LINK') continue;
      out.push(c);
    }
    return out;
  }

  /* A wrapper holding a single child would reveal the whole section as one block, which
     reads as a cheap page fade. Step down through single children until there is more than
     one thing to stagger — that is what separates the heading, the copy and the media. */
  function spread(el, depth) {
    var kids = boxes(el);
    if (kids.length === 1 && depth < 3 && boxes(kids[0]).length > 1) return spread(kids[0], depth + 1);
    return kids;
  }

  function collect(root) {
    var wrappers = root.querySelectorAll('[class*="aurora-w--"]');
    for (var w = 0; w < wrappers.length; w++) {
      var kids = spread(wrappers[w], 0);
      var order = 0;
      for (var k = 0; k < kids.length; k++) {
        var kid = kids[k];
        var group = kid.matches(GROUP) ? kid : kid.querySelector(':scope > ' + GROUP);
        if (group && group.children.length > 1) {
          for (var i = 0; i < group.children.length; i++) mark(group.children[i], i);
        } else if (TEXTBOX.test(kid.className) && boxes(kid).length > 1) {
          /* a header or copy column animates its OWN lines — heading, then subheading, then
             button — instead of sliding in as one slab. This is the text animation; a
             container that moves as a unit reads as a page transition, not as typography. */
          var parts = boxes(kid);
          for (var p = 0; p < parts.length; p++) mark(parts[p], order++);
        } else {
          mark(kid, order++);
        }
      }
    }
  }

  /* Targets are chosen at DOMContentLoaded, before images have sized themselves, so an
     element measured as below the fold can end up ON screen once the hero loads — it then
     sits at opacity 0 in plain view until the visitor scrolls it past the trigger. Once
     layout has settled, anything actually visible is shown immediately, with no animation:
     content already in front of the visitor must never be waiting to appear. */
  function settleInView() {
    var pending = document.querySelectorAll('.' + HIDDEN + ':not(.' + SHOWN + ')');
    for (var i = 0; i < pending.length; i++) {
      var r = pending[i].getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        pending[i].classList.add(SHOWN, INSTANT);
        observer.unobserve(pending[i]);
      }
    }
  }

  function init(root) {
    if (!observer) observer = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -18% 0px' });
    ROOT.classList.add(READY);
    collect(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  if (document.readyState === 'complete') {
    settleInView();
  } else {
    window.addEventListener('load', settleInView);
  }

  /* sections re-rendered by the editor, and content swapped in by the Section Rendering
     API (facets, pagination, quick-buy), arrive after init */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:reorder', function (e) { init(e.target); });
})();
